import {
  ANSWER_MODES,
  ANSWER_MODE_LABELS,
  TEXT_MATCH_LABELS,
  isPointsForm
} from '../constants/formScoring';
import { stripHtmlToText } from './richText';

/** Match AssignmentFormCanvas section order and questionCounter numbering. */
function getFormQuestionDisplayNumbers(form) {
  const questions = Array.isArray(form?.questions) ? form.questions : [];
  const sections = [];
  const sectionMap = new Map();

  questions.forEach((q) => {
    const name = (q.group && String(q.group).trim()) || '';
    if (!sectionMap.has(name)) {
      const entry = { name, questions: [] };
      sectionMap.set(name, entry);
      sections.push(entry);
    }
    sectionMap.get(name).questions.push(q);
  });

  const numbersByQuestionId = new Map();
  let counter = 0;
  sections.forEach((section) => {
    section.questions.forEach((q) => {
      counter += 1;
      if (q?.question_id) {
        numbersByQuestionId.set(q.question_id, counter);
      }
    });
  });
  return numbersByQuestionId;
}

function buildAnswerKeyEntry(question, displayIndex) {
  const scoring = question?.scoring && typeof question.scoring === 'object' ? question.scoring : {};
  const mode = scoring.answerMode || ANSWER_MODES.FREE;
  const label = stripHtmlToText(question?.label) || 'Question';
  const modeLabel = ANSWER_MODE_LABELS[mode] || mode;
  const chips = [];
  let detail = null;

  if (mode === ANSWER_MODES.FREE) {
    detail = 'Any non-empty response earns credit.';
  } else if (mode === ANSWER_MODES.MANUAL) {
    detail = 'Manual grading';
  } else if (mode === ANSWER_MODES.CHOICES) {
    const opts = (scoring.correctOptions || []).map(String).filter(Boolean);
    if (opts.length) {
      opts.forEach((opt) => chips.push({ text: opt, tone: 'correct' }));
    } else {
      detail = 'No correct options configured.';
    }
  } else if (mode === ANSWER_MODES.TEXT) {
    const match = TEXT_MATCH_LABELS[scoring.textRule?.match] || scoring.textRule?.match || 'includes';
    const value = String(scoring.textRule?.value ?? '').trim();
    if (value) {
      chips.push({ text: `${match}: "${value}"`, tone: 'match' });
    } else {
      detail = 'No match text configured.';
    }
  } else if (mode === ANSWER_MODES.NUMBER_EXACT) {
    const exact = scoring.numberRule?.exact;
    if (exact != null && exact !== '') {
      chips.push({ text: String(exact), tone: 'correct' });
    } else {
      detail = 'No exact number configured.';
    }
  } else if (mode === ANSWER_MODES.NUMBER_RANGE) {
    const min = scoring.numberRule?.min;
    const max = scoring.numberRule?.max;
    if (min != null && max != null) {
      chips.push({ text: `${min} – ${max}`, tone: 'range' });
    } else {
      detail = 'No number range configured.';
    }
  } else if (mode === ANSWER_MODES.DATE_EXACT) {
    const exact = scoring.dateRule?.exact;
    if (exact) {
      chips.push({ text: exact, tone: 'correct' });
    } else {
      detail = 'No exact date configured.';
    }
  } else if (mode === ANSWER_MODES.DATE_RANGE) {
    const min = scoring.dateRule?.min;
    const max = scoring.dateRule?.max;
    if (min && max) {
      chips.push({ text: `${min} → ${max}`, tone: 'range' });
    } else {
      detail = 'No date range configured.';
    }
  } else {
    detail = modeLabel;
  }

  return {
    id: question.question_id || `q-${displayIndex}`,
    index: displayIndex,
    label,
    mode,
    modeLabel,
    chips,
    detail
  };
}

/** Whether a points-form question should appear in the answer key sidebar. */
function shouldIncludeInAnswerKey(question, pointsForm) {
  if (!question?.question_id) return false;
  if (!pointsForm) return true;
  const mode = question.scoring?.answerMode || ANSWER_MODES.FREE;
  // Free-response auto-credit has no keyed answer; manual and all other modes are shown.
  return mode !== ANSWER_MODES.FREE;
}

/** Structured answer-key rows for assignment sidebar UI. */
export function getFormAnswerKeyEntries(form) {
  const questions = Array.isArray(form?.questions) ? form.questions : [];
  const points = isPointsForm(form?.type);
  const displayNumbers = getFormQuestionDisplayNumbers(form);

  return questions
    .filter((q) => shouldIncludeInAnswerKey(q, points))
    .map((q) => {
      const displayIndex = displayNumbers.get(q.question_id) ?? 0;
      return buildAnswerKeyEntry(q, displayIndex);
    })
    .filter((entry) => entry.index > 0)
    .sort((a, b) => a.index - b.index);
}

/** @deprecated use getFormAnswerKeyEntries */
export function getFormAnswerKeyLines(form) {
  return getFormAnswerKeyEntries(form).map((entry) => {
    const chipText = entry.chips.map((c) => c.text).join(', ');
    if (chipText) return `${entry.label}: ${chipText}`;
    return `${entry.label}: ${entry.detail || entry.modeLabel}`;
  });
}
